import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { IPatientBaseline } from "../../@types/Baselines";
import { MeasurementTypes } from "../../@types/Measurements";
import {
  colapsedSectionsPropertyNames,
  ICollapsedSectionList,
} from "../../@types/Patient";
import { IPortalPreferences } from "../../@types/Preferences";
import { NewDataPopup } from "../../components/NewDataPopup";
import { queryClient } from "../../config/query";
import { VIEWED_FIRST_PATIENT } from "../../constants/localStorageKeys";
import {
  PatientPageNotificationProvider,
  usePatientPageNotification,
} from "../../contexts/PatientPageNotificationContext";
import { useMarkMeasurementCardsSeen } from "../../hooks/mutations/measurement-cards-seen";
import { useCombinedMeasurementConfigs } from "../../hooks/queries/measurement-configs";
import { usePatientInfo, usePatientWards } from "../../hooks/queries/patients";
import { useChildRoutes } from "../../hooks/useChildRoutes";
import { useToast } from "../../hooks/useToast";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { PageLayout } from "../../layout/components/PageLayout";
import { baselinesService } from "../../services/baselinesService";
import { userService } from "../../services/userService";
import { isUat } from "../../utils/environment";
import { mixpanelActions } from "../../utils/mixpanel";
import { isValidPatientId } from "../../utils/patientId";
import { randomIntFromInterval } from "../../utils/random";
import { scrollToSectionPatientPage } from "../../utils/scrollToSectionPatientPage";
import { safeStorage } from "../../utils/storage";
import {
  isNewAppOnboardingForPatient,
  isWardFeatureEnabledForPatient,
  resolvePatientWardIds,
} from "../../utils/wardConfig";
import { NotFoundContent } from "../NotFound";
import { AlertsSection } from "./components/AlertsSection";
import { AppointmentsSection } from "./components/AppointmentsSection";
import { DevicesSection } from "./components/DevicesSection";
import { FormsSection } from "./components/FormsSection";
import { JournalsSection } from "./components/JournalsSection";
import { LabsSection } from "./components/LabsSection";
import { MeasurementSection } from "./components/MeasurementsSection";
import { MedicationsSection } from "./components/MedicationsSection";
import { MessagesSection } from "./components/MessagesSection";
import { NotesSection } from "./components/NotesSection";
import { PatientLateralMenuDesktop } from "./components/PatientLateralMenu";
import { PatientSubNav } from "./components/PatientSubNav";
import { PinnedEcgSection } from "./components/PinnedEcgSection";
import { PinnedSection } from "./components/PinnedSection";
import { PromsSection } from "./components/PromsSection";
import { ProtocolActionsSection } from "./components/ProtocolActionsSection";
import {
  LateralMenuContainer,
  LoadingOverlay,
  PatientPageContainer,
  PatientPageContent,
  StyledSection,
} from "./PatientPage.styles";
import "react-spring-bottom-sheet/dist/style.css";

dayjs.extend(utc);

function PatientPageNewDataPopup() {
  const { hasNewData, refreshPage, dismissNewData } =
    usePatientPageNotification();

  if (!hasNewData) {
    return null;
  }

  return <NewDataPopup onRefresh={refreshPage} onDismiss={dismissNewData} />;
}

function PatientPageView() {
  const { t } = useTranslation();

  const location = useLocation();
  const { patientId } = useParams();

  const patientPageRef = useRef<null | HTMLDivElement>(null);
  const overviewSectionRef = useRef<null | HTMLDivElement>(null);
  const measurementsSectionRef = useRef<null | HTMLDivElement>(null);
  const alertsSectionRef = useRef<null | HTMLDivElement>(null);
  const promsSectionRef = useRef<null | HTMLDivElement>(null);
  const protocolActionsSectionRef = useRef<null | HTMLDivElement>(null);
  const formsSectionRef = useRef<null | HTMLDivElement>(null);
  const labsSectionRef = useRef<null | HTMLDivElement>(null);
  const notesSectionRef = useRef<null | HTMLDivElement>(null);
  const medicationSectionRef = useRef<null | HTMLDivElement>(null);
  const journalSectionRef = useRef<null | HTMLDivElement>(null);
  const messagesSectionRef = useRef<null | HTMLDivElement>(null);
  const devicesSectionRef = useRef<null | HTMLDivElement>(null);
  const pinnedSectionRef = useRef<null | HTMLDivElement>(null);
  const appointmentsSectionRef = useRef<null | HTMLDivElement>(null);
  const markedPinnedFev1SeenBaselineIdRef = useRef<string | null>(null);
  const announcedMeasurementCardsRef = useRef<Set<string>>(new Set());

  const { patient, isFetching, isRefetching } = usePatientInfo({ patientId });
  // EN-4960: the patient's full ward set drives feature flags (unioned across wards).
  const { patientWards, isPatientWardsFetched } = usePatientWards(patient?.id);

  const { hideChildRoute, showChildRoute } = useChildRoutes();

  const {
    measurementsConfigs,
    labResultsConfigs,
    measurementCardsChanged,
    measurementCardsToken,
    isFetching: isConfigsFetching,
    isLoading: isConfigsLoading,
  } = useCombinedMeasurementConfigs({
    patientId: patient?.id,
  });

  const { portalPreferences, changePortalPreferences } = useUserPreferences();
  const { showToast } = useToast();
  const { mutate: markMeasurementCardsSeen } = useMarkMeasurementCardsSeen();

  document.title = t("patientPage_documentTitle");

  const [patientCollapsedSettingsIndex, setPatientCollapsedSettingsIndex] =
    useState<number>(-1);
  const [isPatientLateralMenuOpen, setIsPatientLateralMenuOpen] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasPinnedGraph, setHasPinnedGraph] = useState(false);
  const [pinnedGraphKey, setPinnedGraphKey] = useState("123456");
  const [hasMedication, setHasMedication] = useState(true);
  const [hasProtocolActions, setHasProtocolActions] = useState(false);
  const [openFev1Graph, setOpenFev1Graph] = useState(false);
  const [fev1ISHLTBaseline, setFev1ISHLTBaseline] =
    useState<IPatientBaseline>();

  const defaultCollapsedSection = {
    value: false,
    collapsedDate: "",
  };

  const [collapsedSections, setCollapsedSections] =
    useState<ICollapsedSectionList>({
      measurementSection: defaultCollapsedSection,
      labsSection: defaultCollapsedSection,
      alertsSection: defaultCollapsedSection,
      promsSection: defaultCollapsedSection,
      protocolActionsSection: defaultCollapsedSection,
      formsSection: defaultCollapsedSection,
      medicationsSection: defaultCollapsedSection,
      journalsSection: defaultCollapsedSection,
      notesSection: defaultCollapsedSection,
      messagesSection: defaultCollapsedSection,
      devicesSection: defaultCollapsedSection,
      pinnedSection: defaultCollapsedSection,
      appointmentsSection: defaultCollapsedSection,
    });

  useEffect(() => {
    const loadPreferences = async () => {
      const pfResponse = await userService.getPortalPreferences();
      changePortalPreferences(pfResponse.data);

      if (portalPreferences.collapsedSectionsConfigList) {
        const parsedPatientCollapsedSettingsList = JSON.parse(
          portalPreferences.collapsedSectionsConfigList
        );

        const patientCollapsedSettingsIndex =
          parsedPatientCollapsedSettingsList.findIndex(
            (element: {
              patientId: number;
              ICollapsedSections: ICollapsedSectionList;
            }) => element.patientId.toString() === patientId
          );

        if (patientCollapsedSettingsIndex > -1) {
          setPatientCollapsedSettingsIndex(patientCollapsedSettingsIndex);

          setCollapsedSections(
            parsedPatientCollapsedSettingsList[patientCollapsedSettingsIndex]
              .collapsedSectionsConfig
          );
        }
      }
    };

    loadPreferences();
  }, []);

  useEffect(() => {
    // Wait until the patient has loaded AND the wards query has SETTLED (success or error).
    // On error patientWards stays undefined forever (retry 1, no refetch); gating must still run
    // rather than leaving the Medication tab visible, so we proceed once settled and let
    // resolvePatientWardIds fall back to the primary ward (hospitalId).
    if (patient === undefined || !isPatientWardsFetched) return;

    // EN-4960: union across the patient's wards, falling back to the primary ward (hospitalId).
    const patientWardIds = resolvePatientWardIds(
      patientWards?.wardIds,
      patient.hospitalId
    );

    const loadMedication = () => {
      // EN-5199: gate in both directions — hidden route names persist in
      // ChildRoutesContext across patient navigation, so the menu item must be
      // explicitly restored for patients whose wards do enable medication.
      if (
        !isWardFeatureEnabledForPatient(
          patientWardIds,
          "enablePatientMedication"
        )
      ) {
        setHasMedication(false);
        hideChildRoute("Medication");
      } else {
        setHasMedication(true);
        showChildRoute("Medication");
      }
    };

    const checkForNewFev1ISHLTBaselines = async () => {
      if (
        isWardFeatureEnabledForPatient(patientWardIds, "enableISHLTBaselines")
      ) {
        const latestISHLTFev1Baseline = (
          await baselinesService.checkForNewISHLTBaselines(patient.id, "fev1")
        ).data;

        setFev1ISHLTBaseline(latestISHLTFev1Baseline);
      }
    };

    loadMedication();
    checkForNewFev1ISHLTBaselines();
  }, [patient, patientWards, isPatientWardsFetched]);

  useEffect(() => {
    // EN-5134: an admin reconfigured the measurement cards this clinician sees. Acknowledged as soon
    // as the notice is shown, not on dismiss - the toast auto-closes and may never be clicked.
    if (!patient?.id || !measurementCardsChanged || !measurementCardsToken) {
      return;
    }

    // Keyed on patient and token, not a single slot: PatientPage does not remount when :patientId
    // changes, so a plain "last token" guard would let A -> B -> A announce twice.
    const announcementKey = `${patient.id}:${measurementCardsToken}`;
    if (announcedMeasurementCardsRef.current.has(announcementKey)) {
      return;
    }

    // Claimed before the request, so a re-render or React's double-invoked effect cannot fire two
    // toasts while it is in flight; released on failure so the notice is not lost for the session
    // while the server still has the change pending.
    announcedMeasurementCardsRef.current.add(announcementKey);

    // Longer than the container's 2.5s default: this is a notice to read, not an acknowledgement of
    // something the clinician just did.
    showToast(t("patientPage_toasts_cardsUpdated"), "info", {
      autoClose: 6000,
    });

    markMeasurementCardsSeen(
      { patientId: patient.id, token: measurementCardsToken },
      {
        onError: (error) => {
          announcedMeasurementCardsRef.current.delete(announcementKey);
          console.error(t("patientPage_toasts_cardsAckFailed"), error);
        },
      }
    );
  }, [
    patient?.id,
    measurementCardsChanged,
    measurementCardsToken,
    showToast,
    markMeasurementCardsSeen,
  ]);

  useEffect(() => {
    // EN-3346: the Protocol actions table + menu item show only when the
    // patient belongs to a "NewApp" ward; a "LegacyApp"-only patient sees
    // neither. Wait until the wards query has settled before deciding.
    if (patient === undefined || !isPatientWardsFetched) {
      return;
    }

    const patientWardIds = resolvePatientWardIds(
      patientWards?.wardIds,
      patient.hospitalId
    );

    const isNewApp = isNewAppOnboardingForPatient(patientWardIds);
    setHasProtocolActions(isNewApp);

    // EN-5199: hide/show are idempotent on a Set of hidden names now, so the
    // old "re-hide on every childRoutes change" self-healing (a workaround for
    // showChildRoute rebuilding the menu from base) is no longer needed.
    if (!isNewApp) {
      hideChildRoute("Protocol actions");
    } else {
      showChildRoute("Protocol actions");
    }
  }, [patient, patientWards, isPatientWardsFetched]);

  const handleOnChangeCollapsedSections = async (
    updatedCollapsedSectionsConfig: ICollapsedSectionList
  ) => {
    let updatedCollapsedSectionsConfigList = [];

    if (portalPreferences.collapsedSectionsConfigList) {
      updatedCollapsedSectionsConfigList = JSON.parse(
        portalPreferences.collapsedSectionsConfigList
      );
      if (patientCollapsedSettingsIndex !== -1) {
        updatedCollapsedSectionsConfigList[
          patientCollapsedSettingsIndex
        ].collapsedSectionsConfig = updatedCollapsedSectionsConfig;
      } else {
        updatedCollapsedSectionsConfigList.push({
          patientId: patient.id,
          collapsedSectionsConfig: updatedCollapsedSectionsConfig,
        });

        const updatedPatientCollapsedSettingsIndex =
          updatedCollapsedSectionsConfigList.length - 1;

        setPatientCollapsedSettingsIndex(updatedPatientCollapsedSettingsIndex);
      }
    } else {
      updatedCollapsedSectionsConfigList.push({
        patientId: patient.id,
        collapsedSectionsConfig: updatedCollapsedSectionsConfig,
      });
      setPatientCollapsedSettingsIndex(0);
    }

    const parsedUpdatedCollapsedSectionsConfigList = JSON.stringify(
      updatedCollapsedSectionsConfigList
    );

    const newPortalPreferences: IPortalPreferences = {
      ...portalPreferences,
      collapsedSectionsConfigList: parsedUpdatedCollapsedSectionsConfigList,
    };
    changePortalPreferences(newPortalPreferences);
    await userService.updateCollapsedSectionsConfig(
      parsedUpdatedCollapsedSectionsConfigList
    );
  };

  const updateColapsedSections = (section: colapsedSectionsPropertyNames) => {
    const formatedSectionString =
      section.charAt(0).toUpperCase() +
      section.slice(1).replace(/([a-z])([A-Z])/g, "$1 $2");

    setCollapsedSections((prevObject) => {
      const updatedCollapsedValue = !prevObject[section]?.value;
      mixpanelActions.track(
        `UserAction: ${
          updatedCollapsedValue ? "Collapse" : "Expand"
        } ${formatedSectionString}`
      );

      const updatedCollapsedSectionsConfig = {
        ...prevObject,
        [section]: {
          value: updatedCollapsedValue,
          collapsedDate: updatedCollapsedValue ? dayjs.utc().toISOString() : "",
        },
      };

      handleOnChangeCollapsedSections(updatedCollapsedSectionsConfig);
      return updatedCollapsedSectionsConfig;
    });
  };

  useEffect(() => {
    if (isConfigsFetching) {
      return;
    }

    if (!measurementsConfigs || measurementsConfigs.length === 0) {
      hideChildRoute("Measurements");
    } else {
      showChildRoute("Measurements");
    }
  }, [isConfigsFetching, measurementsConfigs]);

  useEffect(() => {
    if (isConfigsFetching) {
      return;
    }

    if (!labResultsConfigs || labResultsConfigs.length === 0) {
      hideChildRoute("Labs");
    } else {
      showChildRoute("Labs");
    }
  }, [isConfigsFetching, labResultsConfigs]);

  useEffect(() => {
    mixpanelActions.track("Visit Screen: Patient Page");
    const viewedFirstPatient = safeStorage.getItem(VIEWED_FIRST_PATIENT);
    if (viewedFirstPatient && viewedFirstPatient === "no") {
      setIsPatientLateralMenuOpen(true);
    }
    safeStorage.setItem(VIEWED_FIRST_PATIENT, "yes");

    return () => {
      queryClient.invalidateQueries({
        queryKey: [`patient-info-${patientId}`],
      });
      queryClient.invalidateQueries({
        queryKey: [`patient-monitoring-info-${patientId}`],
      });
    };
  }, [patientId]);

  useEffect(() => {
    if (measurementsConfigs === null || labResultsConfigs === null) {
      return;
    }

    scrollToSectionPatientPage({
      overviewSectionRef,
      measurementsSectionRef,
      labsSectionRef,
      alertsSectionRef,
      promsSectionRef,
      protocolActionsSectionRef,
      formsSectionRef,
      notesSectionRef,
      medicationSectionRef,
      journalSectionRef,
      messagesSectionRef,
      devicesSectionRef,
      pinnedSectionRef,
      appointmentsSectionRef,
    });
  }, [location.pathname, measurementsConfigs, labResultsConfigs]);

  const handleOnClickToExpandLateralMenu = () => {
    setIsPatientLateralMenuOpen((prevState) => !prevState);
  };

  const handleOnEditingClick = (isEditing: boolean) => {
    setIsEditing(isEditing);
  };

  useEffect(() => {
    if (!isConfigsFetching) {
      const allConfigs = [...measurementsConfigs, ...labResultsConfigs];

      // ECG is not a measurement/lab config, so it's accepted explicitly; the
      // ECG table view is rendered in the pinned slot instead of PinnedSection.
      const isEcgPinned = portalPreferences.pinnedMeasurementType === "ecg";

      // EN-5042: the Breathlessness (mMRC) graph is hidden on UAT, so a
      // previously-pinned breathlessness card must not render either.
      const isBreathlessnessHiddenOnUat =
        isUat() && portalPreferences.pinnedMeasurementType === "dyspnea";

      const isPinnedGraphValid =
        (isEcgPinned ||
          allConfigs.some(
            (config) => config.type === portalPreferences.pinnedMeasurementType
          )) &&
        portalPreferences.pinnedMeasurementType !== undefined &&
        portalPreferences.pinnedMeasurementType !== "" &&
        !isBreathlessnessHiddenOnUat;

      if (!isPinnedGraphValid) {
        hideChildRoute("Pinned");
      } else {
        showChildRoute("Pinned");
      }

      setHasPinnedGraph(isPinnedGraphValid);
    }
  }, [
    isConfigsFetching,
    measurementsConfigs,
    labResultsConfigs,
    portalPreferences.pinnedMeasurementType,
  ]);

  useEffect(() => {
    const randomKey = randomIntFromInterval(1, 1000000).toString();

    setPinnedGraphKey(randomKey);
  }, [portalPreferences.pinnedMeasurementType]);

  useEffect(() => {
    if (
      portalPreferences.pinnedMeasurementType !== "fev1" ||
      !fev1ISHLTBaseline?.id ||
      !pinnedSectionRef.current
    ) {
      return undefined;
    }

    const baselineId = fev1ISHLTBaseline.id;
    const currentPatientId = patient.id;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          markedPinnedFev1SeenBaselineIdRef.current !== baselineId
        ) {
          markedPinnedFev1SeenBaselineIdRef.current = baselineId;
          baselinesService.markISHLTBaselineAsSeen(
            currentPatientId,
            baselineId,
            "fev1"
          );
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(pinnedSectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [
    portalPreferences.pinnedMeasurementType,
    fev1ISHLTBaseline?.id,
    hasPinnedGraph,
    isFetching,
  ]);

  const handleFev1GraphOpen = async () => {
    if (fev1ISHLTBaseline?.id) {
      await baselinesService.markISHLTBaselineAsSeen(
        patient.id,
        fev1ISHLTBaseline.id,
        "fev1"
      );
    }
  };

  const handleOnISHLTFevChipClick = () => {
    if (portalPreferences.pinnedMeasurementType === "fev1") {
      pinnedSectionRef.current?.scrollIntoView({
        behavior: "smooth",
      });
      // Mark as seen is handled by the IntersectionObserver on pinnedSectionRef
    } else {
      setOpenFev1Graph(true);
      // Mark as seen is handled by MeasurementCard's onFev1GraphOpen callback
    }
  };

  // Show loading overlay for content, but always render PatientSubNav with its skeleton
  const isPageLoading = (isFetching && !isRefetching) || isConfigsLoading;

  return (
    <PatientPageNotificationProvider patientId={patientId}>
      <PatientPageContainer ref={patientPageRef}>
        <PatientSubNav
          isLateralPatientMenuOpenOnDesktop={isPatientLateralMenuOpen}
          ishltFev1Baseline={fev1ISHLTBaseline?.baseline}
          onISHLTFev1ChipClick={handleOnISHLTFevChipClick}
        />

        {isPageLoading || !collapsedSections ? (
          <LoadingOverlay />
        ) : (
          <>
            <PatientPageContent
              isLateralPatientMenuOpenOnDesktop={isPatientLateralMenuOpen}
            >
              <PageLayout extraPadding>
                {hasPinnedGraph &&
                portalPreferences.pinnedMeasurementType !== "" &&
                portalPreferences.pinnedMeasurementType !== undefined ? (
                  <StyledSection
                    key={pinnedGraphKey}
                    ref={pinnedSectionRef}
                    positionRelative
                  >
                    {portalPreferences.pinnedMeasurementType === "ecg" ? (
                      <PinnedEcgSection
                        collapsedSection={
                          collapsedSections.pinnedSection ??
                          defaultCollapsedSection
                        }
                        updateColapsedSections={updateColapsedSections}
                        patient={patient}
                      />
                    ) : (
                      <PinnedSection
                        collapsedSection={
                          collapsedSections.pinnedSection ??
                          defaultCollapsedSection
                        }
                        updateColapsedSections={updateColapsedSections}
                        patient={patient}
                        measurementType={
                          portalPreferences.pinnedMeasurementType as MeasurementTypes
                        }
                      />
                    )}
                  </StyledSection>
                ) : null}

                <StyledSection ref={measurementsSectionRef}>
                  <MeasurementSection
                    collapsedSection={collapsedSections.measurementSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                    configs={measurementsConfigs}
                    openFev1Graph={openFev1Graph}
                    onFev1GraphOpen={handleFev1GraphOpen}
                  />
                </StyledSection>

                <StyledSection
                  css={{ "@sm": { marginBottom: 90 } }}
                  ref={labsSectionRef}
                >
                  <LabsSection
                    collapsedSection={collapsedSections.labsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                    configs={labResultsConfigs}
                  />
                </StyledSection>

                <StyledSection ref={alertsSectionRef} positionRelative>
                  <AlertsSection
                    collapsedSection={collapsedSections.alertsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={appointmentsSectionRef} positionRelative>
                  <AppointmentsSection
                    collapsedSection={
                      collapsedSections.appointmentsSection ??
                      defaultCollapsedSection
                    }
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={promsSectionRef} positionRelative>
                  <PromsSection
                    collapsedSection={collapsedSections.promsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                {hasProtocolActions ? (
                  <StyledSection
                    ref={protocolActionsSectionRef}
                    positionRelative
                  >
                    <ProtocolActionsSection
                      collapsedSection={
                        collapsedSections.protocolActionsSection ??
                        defaultCollapsedSection
                      }
                      updateColapsedSections={updateColapsedSections}
                      patient={patient}
                    />
                  </StyledSection>
                ) : null}

                <StyledSection ref={formsSectionRef} positionRelative>
                  <FormsSection
                    collapsedSection={collapsedSections.formsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                {hasMedication ? (
                  <StyledSection ref={medicationSectionRef} positionRelative>
                    <MedicationsSection
                      collapsedSection={collapsedSections.medicationsSection}
                      updateColapsedSections={updateColapsedSections}
                      patient={patient}
                    />
                  </StyledSection>
                ) : null}

                <StyledSection ref={journalSectionRef} positionRelative>
                  <JournalsSection
                    collapsedSection={collapsedSections.journalsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={notesSectionRef} positionRelative>
                  <NotesSection
                    collapsedSection={collapsedSections.notesSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={messagesSectionRef} positionRelative>
                  <MessagesSection
                    collapsedSection={collapsedSections.messagesSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={devicesSectionRef} positionRelative>
                  <DevicesSection
                    collapsedSection={collapsedSections.devicesSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>
              </PageLayout>
            </PatientPageContent>

            <LateralMenuContainer isEditing={isEditing}>
              <PatientLateralMenuDesktop
                patientId={patientId}
                open={isPatientLateralMenuOpen}
                onExpandClick={handleOnClickToExpandLateralMenu}
                onEditingClick={handleOnEditingClick}
              />
            </LateralMenuContainer>

            <PatientPageNewDataPopup />
          </>
        )}
      </PatientPageContainer>
    </PatientPageNotificationProvider>
  );
}

/**
 * EN-5318. Anything under `/patients/` that is not a list route and not a
 * numeric id (e.g. `/patients/null`) would otherwise load a patient that cannot
 * exist and crash into the error screen. Show the 404 page directly.
 */
export function PatientPage() {
  const { patientId } = useParams();

  if (!isValidPatientId(patientId)) {
    return <NotFoundContent />;
  }

  return <PatientPageView />;
}
